import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { acBase, Spinner, WAIcon, ADMIN_WA } from './academyUtils';
import { hashPassword, validateEmail, validatePassword, passwordStrength } from '../../utils/auth';
import { useLanguage } from '../../context/LanguageContext';

const STRENGTH_COLOR = { weak:'#e74c3c', medium:'#e67e22', strong:'#27ae60' };
const STRENGTH_LABEL = { weak:'Weak', medium:'Fair', strong:'Strong' };

const DEFAULT_REG_COURSES = ['IELTS','General English','German','SAT','Interview & Demo Preparation','TEFL','Other'];

export default function StudentRegister({ onSuccess, onLogin, onBack }) {
  const { t } = useLanguage();
  const [name,          setName]          = useState('');
  const [email,         setEmail]         = useState('');
  const [whatsapp,      setWhatsapp]      = useState('');
  const [password,      setPassword]      = useState('');
  const [courses,       setCourses]       = useState([]);
  const [selectedCourse,setSelectedCourse]= useState(null);
  const [confirmPwd,    setConfirmPwd]    = useState('');
  const [terms,       setTerms]       = useState(false);
  const [loading,     setLoading]     = useState(false);
  const [error,       setError]       = useState('');
  const [registered,  setRegistered]  = useState(false);

  const strength = password ? passwordStrength(password) : null;

  useEffect(() => {
    async function loadCourses() {
      try {
        const { data } = await supabase
          .from('academy_settings')
          .select('setting_value')
          .eq('setting_key', 'registration_courses')
          .single();
        const parsed = data?.setting_value
          ? (() => { try { return JSON.parse(data.setting_value); } catch { return null; } })()
          : null;
        setCourses(Array.isArray(parsed) ? parsed : DEFAULT_REG_COURSES);
      } catch {
        setCourses(DEFAULT_REG_COURSES);
      }
    }
    loadCourses();
  }, []);

  async function handleRegister() {
    setError('');
    if (!name.trim())            { setError(t('fieldRequired') + ' — ' + t('fullName')); return; }
    if (!validateEmail(email))   { setError(t('invalidEmail')); return; }
    if (!validatePassword(password)) { setError(t('invalidPassword')); return; }
    if (password !== confirmPwd) { setError(t('passwordMismatch')); return; }
    if (!terms)                  { setError(t('termsAgree') + ' to continue.'); return; }

    setLoading(true);
    try {
      const hash = await hashPassword(password);
      const wa   = whatsapp.trim().replace(/\D/g, '');

      const { error: err } = await supabase
        .from('students')
        .insert({
          name:             name.trim(),
          email:            email.trim().toLowerCase(),
          whatsapp:         wa || null,
          password_hash:    hash,
          active:           false,
          subscription_status: 'inactive',
          ...(selectedCourse ? { notes: `Interested in: ${selectedCourse}` } : {}),
        });

      if (err) {
        if (err.code === '23505') {
          setError(err.message?.includes('email')
            ? 'This email is already registered. Please log in instead.'
            : 'This WhatsApp number is already registered.');
        } else {
          throw err;
        }
        setLoading(false);
        return;
      }
      setRegistered(true);
    } catch {
      setError('Registration failed. Please try again.');
    } finally { setLoading(false); }
  }

  if (registered) {
    return (
      <div style={{ minHeight:'100vh', background:'linear-gradient(135deg,#f0f4f8,#e4ecf7)', display:'flex', alignItems:'center', justifyContent:'center', padding:20 }}>
        <div style={{ width:'100%', maxWidth:400 }}>
          <div style={{ textAlign:'center', marginBottom:24 }}>
            <img src="/logo.png" alt="IELTS Compass" style={{ height:48, objectFit:'contain' }}/>
          </div>
          <div style={{ ...acBase.card, textAlign:'center' }}>
            <div style={{ fontSize:42, marginBottom:12 }}>✅</div>
            <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:22, fontWeight:700, color:'#1a2a4a', marginBottom:10 }}>
              Registration Received!
            </div>
            <div style={{ fontSize:13, color:'#5a6a88', lineHeight:1.7, marginBottom:20 }}>
              {t('registrationSuccess')}
            </div>
            <a href={`https://wa.me/${ADMIN_WA}`} target="_blank" rel="noreferrer"
              style={{ ...acBase.btnWA, display:'inline-flex', justifyContent:'center', textDecoration:'none', marginBottom:16, width:'100%', boxSizing:'border-box' }}>
              <WAIcon size={16}/>
              Message Mr. Ahmed Salem →
            </a>
            <div style={{ textAlign:'center' }}>
              <button onClick={onLogin} style={{ ...acBase.back, fontSize:13, color:'#1a5fa8', textDecoration:'underline' }}>
                ← Back to Login
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight:'100vh', background:'linear-gradient(135deg,#f0f4f8,#e4ecf7)', display:'flex', alignItems:'center', justifyContent:'center', padding:20 }}>
      <div style={{ width:'100%', maxWidth:440 }}>
        <div style={{ textAlign:'center', marginBottom:28 }}>
          <img src="/logo.png" alt="IELTS Compass" style={{ height:56, objectFit:'contain', marginBottom:10 }}/>
          <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:20, fontWeight:700, color:'#1a2a4a', lineHeight:1.1 }}>
            IELTS<span style={{ color:'#b8860b' }}>COMPASS</span>
          </div>
          <div style={{ fontSize:11, color:'#5a6a88', fontFamily:"'JetBrains Mono',monospace", letterSpacing:'.14em', textTransform:'uppercase', marginTop:4 }}>Student Registration</div>
        </div>

        <div style={acBase.card}>
          <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:22, fontWeight:700, color:'#1a2a4a', marginBottom:4 }}>Create Account</div>
          <div style={{ fontSize:13, color:'#5a6a88', marginBottom:6, lineHeight:1.6 }}>
            Register to book lessons with our teachers.
          </div>
          <div style={{ background:'#fff8e8', border:'1px solid #f0d890', borderRadius:9, padding:'10px 14px', marginBottom:18, fontSize:12.5, color:'#7a4a00', lineHeight:1.6 }}>
            ⚠️ Your account will be reviewed and activated by Mr. Ahmed Salem before you can log in.
          </div>

          <label style={acBase.lbl}>{t('fullName')} *</label>
          <input value={name} onChange={e=>setName(e.target.value)} placeholder="Ahmed Hassan" style={acBase.inp} autoFocus/>

          <label style={acBase.lbl}>{t('emailAddress')} *</label>
          <input type="email" value={email} onChange={e=>setEmail(e.target.value)} placeholder="yourname@email.com" style={acBase.inp}/>

          <label style={acBase.lbl}>{t('whatsapp')} (optional)</label>
          <input value={whatsapp} onChange={e=>setWhatsapp(e.target.value)} placeholder="201012345678" style={acBase.inp}/>

          <label style={acBase.lbl}>Password *</label>
          <input type="password" value={password} onChange={e=>setPassword(e.target.value)} placeholder="••••••••" style={acBase.inp}/>
          {password && (
            <div style={{ display:'flex', alignItems:'center', gap:8, marginTop:-8, marginBottom:10 }}>
              <div style={{ flex:1, height:4, background:'#e8edf5', borderRadius:2, overflow:'hidden' }}>
                <div style={{ height:'100%', width:strength==='strong'?'100%':strength==='medium'?'65%':'30%', background:STRENGTH_COLOR[strength], borderRadius:2, transition:'width .3s, background .3s' }}/>
              </div>
              <span style={{ fontSize:11, color:STRENGTH_COLOR[strength], fontWeight:600, fontFamily:"'JetBrains Mono',monospace" }}>{STRENGTH_LABEL[strength]}</span>
            </div>
          )}

          <label style={acBase.lbl}>{t('confirmPassword')} *</label>
          <input type="password" value={confirmPwd} onChange={e=>setConfirmPwd(e.target.value)}
            onKeyDown={e=>e.key==='Enter'&&handleRegister()} placeholder="••••••••" style={acBase.inp}/>

          {/* Course interest picker */}
          {courses.length > 0 && (
            <div style={{ marginBottom:16 }}>
              <label style={acBase.lbl}>Interested In (optional)</label>
              <div style={{ display:'flex', flexWrap:'wrap', gap:8 }}>
                {courses.map(course => (
                  <button key={course} type="button" onClick={() => setSelectedCourse(selectedCourse === course ? null : course)}
                    style={{ padding:'8px 14px', borderRadius:8, border:`1.5px solid ${selectedCourse===course?'#b8860b':'#dde3ed'}`, background:selectedCourse===course?'#fff8e8':'transparent', color:selectedCourse===course?'#b8860b':'#5a6a88', cursor:'pointer', fontSize:13, fontWeight:selectedCourse===course?600:400 }}>
                    {course}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Terms checkbox */}
          <label style={{ display:'flex', alignItems:'flex-start', gap:10, marginBottom:16, cursor:'pointer' }}>
            <input type="checkbox" checked={terms} onChange={e=>setTerms(e.target.checked)}
              style={{ marginTop:2, width:16, height:16, flexShrink:0, cursor:'pointer', accentColor:'#b8860b' }}/>
            <span style={{ fontSize:12.5, color:'#5a6a88', lineHeight:1.55 }}>
              {t('termsAgree')}
            </span>
          </label>

          {error && <div style={acBase.err}>{error}</div>}

          <button onClick={handleRegister} disabled={loading}
            style={{ ...acBase.btn, opacity:loading?0.7:1, display:'flex', alignItems:'center', justifyContent:'center', gap:8, marginBottom:12 }}>
            {loading ? <Spinner/> : `${t('register')} →`}
          </button>

          <div style={{ textAlign:'center', fontSize:13, color:'#5a6a88' }}>
            Already have an account?{' '}
            <button onClick={onLogin} style={{ background:'none', border:'none', color:'#b8860b', cursor:'pointer', fontSize:13, fontWeight:600, textDecoration:'underline', padding:0 }}>
              Log in here
            </button>
          </div>
          <div style={{ textAlign:'center', marginTop:12 }}>
            <button onClick={onBack} style={acBase.back}>← Back</button>
          </div>
        </div>
      </div>
    </div>
  );
}
